import { Box } from "@mui/material";
import { Chart as ChartJS, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { dayjs } from "utils/custom-date";

import { ISummary } from "./types";

ChartJS.register(...registerables, ChartDataLabels);

interface IProps {
  beneficiaries: ISummary[];
}

interface IWeekData {
  start: string;
  end: string;
  text: string;
}

interface IWeeklyCount {
  week: string;
  employees: number;
  dependents: number;
}

export function GraphOvertime({ beneficiaries }: IProps) {
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // Gera as últimas 5 semanas
  const weekIntervals: IWeekData[] = useMemo(() => {
    const intervals: IWeekData[] = [];
    const today = dayjs();

    for (let i = 4; i >= 0; i--) {
      const weekStart = today.subtract(i, "week").startOf("week");
      const weekEnd = weekStart.endOf("week");

      const text = `${weekStart.format("DD [de] MMMM")} até ${weekEnd.format(
        "DD [de] MMMM"
      )} de ${weekEnd.format("YYYY")}`;

      intervals.push({
        start: weekStart.format("DD/MM/YYYY"),
        end: weekEnd.format("DD/MM/YYYY"),
        text: text.replace(/ de 2024/g, "").replace(/ de 2025/g, ""),
      });
    }

    return intervals;
  }, []);

  // Função para verificar se uma data está no intervalo
  const isDateInRange = (
    dateToCheck: string,
    startDate: string,
    endDate: string
  ): boolean => {
    const startDateObj = dayjs(startDate, "DD/MM/YYYY");
    const endDateObj = dayjs(endDate, "DD/MM/YYYY");
    const dateToCheckObj = dayjs(dateToCheck);

    return dateToCheckObj.isBetween(startDateObj, endDateObj, "day", "[]");
  };

  // Processa os dados por semana com totais cumulativos
  const weeklyData: IWeeklyCount[] = useMemo(() => {
    let cumulativeEmployees = 0;
    let cumulativeDependents = 0;

    return weekIntervals.map((week) => {
      // Filtra pessoas adicionadas até o final desta semana
      const beneficiariesUpToThisWeek = beneficiaries.filter((beneficiary) => {
        const beneficiaryDate = dayjs(beneficiary.createdAt);
        const weekEndDate = dayjs(week.end, "DD/MM/YYYY");
        return beneficiaryDate.isSameOrBefore(weekEndDate, "day");
      });

      const totalEmployees = beneficiariesUpToThisWeek.filter(
        (b) => b.type === "EMPLOYEE"
      ).length;
      const totalDependents = beneficiariesUpToThisWeek.filter(
        (b) => b.type === "DEPENDENT"
      ).length;

      cumulativeEmployees = totalEmployees;
      cumulativeDependents = totalDependents;

      return {
        week: week.text,
        employees: cumulativeEmployees,
        dependents: cumulativeDependents,
      };
    });
  }, [beneficiaries, weekIntervals]);

  useEffect(() => {
    const labels = weeklyData.map((item) => item.week);
    const employeesData = weeklyData.map((item) => item.employees);
    const dependentsData = weeklyData.map((item) => item.dependents);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: "Titulares",
          data: employeesData,
          backgroundColor: "#3300FF",
          borderWidth: 1,
          barThickness: 120,
          maxBarThickness: 120,
        },
        {
          label: "Dependentes",
          data: dependentsData,
          backgroundColor: "#F3AF3D",
          borderWidth: 1,
          borderRadius: { topLeft: 10, topRight: 10 },
          barThickness: 120,
          maxBarThickness: 120,
        },
      ],
    });
  }, [weeklyData]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          paddingTop: "0.25rem",
          paddingBottom: "1rem",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: "Sora",
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: "2rem",
            color: "rgba(15, 15, 30, 0.6)",
          }}
        >
          Total de Titulares e Dependentes Potenciais no Tempo
        </span>
      </Box>
      <Box
        sx={{
          padding: "1rem",
          flexGrow: 1,
          minHeight: 600,
          position: "relative",
        }}
      >
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              datalabels: {
                display: false,
              },
              legend: {
                display: true,
                position: "top",
                align: "end",
                labels: {
                  usePointStyle: true,
                  color: "#1E1B39",
                  font: {
                    weight: "bold",
                    size: 14,
                    lineHeight: 18,
                  },
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                grid: {
                  display: false,
                },
                ticks: {
                  color: "#423f56",
                },
              },
              y: {
                stacked: true,
                grid: {
                  display: true,
                  borderDash: [10, 10],
                  color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                  color: "#1E1B39",
                },
              },
            },
            animation: {
              duration: 0,
            },
            onHover: undefined,
            onResize: undefined,
          }}
        />
      </Box>
    </>
  );
}
